import { Ban, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'

import { IntegrationKey } from '@ankor-io/common/types/integrations/types'
import type { XeroSyncJobRunPayload } from '@ankor-io/common/types/integrations/xero/types'
import {
  Badge,
  Button,
  CopyToClipboard,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import {
  useQuickBooksSyncJobRunsQuery,
  useRetryXeroConnectionSyncMutation,
  useXeroConnectionsQuery,
  useXeroSyncJobRunsQuery,
} from '@/queries/organization'

import { BackHeader } from './integration-back-header'

// ─── Helpers ────────────────────────────────────────────────────────────────

function runStatusIcon(status: string) {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="size-4 text-green-500" />
    case 'FAILED':
      return <XCircle className="size-4 text-red-500" />
    case 'IN_PROGRESS':
      return <Loader2 className="size-4 text-yellow-500 animate-spin" />
    default:
      return <Ban className="size-4 text-gray-400" />
  }
}

function formatDate(iso: string) {
  const d = new Date(iso)
  const now = new Date()
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  if (d.toDateString() === now.toDateString()) return `Today, ${time}`
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
}

function formatRunType(type: string) {
  return type
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
}

// ─── Main view ──────────────────────────────────────────────────────────────

export function IntegrationRunsView({
  organizationId,
  jobId,
  integrationKey,
  onBack,
  onSelectRun,
}: {
  organizationId: string
  jobId: string
  integrationKey: IntegrationKey
  onBack: () => void
  onSelectRun?: (runId: string) => void
}) {
  const isXero = integrationKey === IntegrationKey.XERO_EXPENSES
  const { data: connections } = useXeroConnectionsQuery(organizationId, { enabled: isXero })

  const connectionId = connections?.[0]?.id
  const xeroRuns = useXeroSyncJobRunsQuery(organizationId, connectionId, jobId, { enabled: isXero })
  const quickBooksRuns = useQuickBooksSyncJobRunsQuery(organizationId, jobId)
  const { mutateAsync: retrySync, isPending: isRetrying } = useRetryXeroConnectionSyncMutation(
    organizationId,
    connectionId,
    jobId,
  )
  const loaded = {
    [IntegrationKey.XERO_EXPENSES]: {
      name: 'Xero',
      runs: xeroRuns.data,
      isPending: xeroRuns.isPending,
      error: xeroRuns.error,
      canRerun: true,
    },
    [IntegrationKey.QUICKBOOKS]: {
      name: 'QuickBooks',
      runs: quickBooksRuns.data,
      isPending: quickBooksRuns.isPending,
      error: quickBooksRuns.error,
      canRerun: false,
    },
  }
  const { name, runs, isPending, error, canRerun } = loaded[integrationKey]

  const handleRerun = () => {
    const promise = retrySync()
    toast.promise(promise, {
      loading: 'Re-running sync...',
      success: 'Sync re-run started',
      error: (err) => `Failed to re-run sync. ${err instanceof Error ? err.message : 'Please try again.'}`,
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <BackHeader
          label={`${name} — Runs`}
          onBack={onBack}
        />
        {canRerun && (
          <Button
            outline
            disabled={isRetrying}
            onClick={handleRerun}
          >
            <RefreshCw
              className={`size-4 ${isRetrying ? 'animate-spin' : ''}`}
              data-slot="icon"
            />
            Re-run
          </Button>
        )}
      </div>

      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}

      {runs && runs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No runs found.</p>}

      {runs && runs.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Run</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Created At</TableHeader>
              <TableHeader>Completed At</TableHeader>
              <TableHeader>Action</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run: XeroSyncJobRunPayload) => (
              <TableRow
                key={run.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => onSelectRun?.(run.id)}
              >
                <TableCell className="font-mono text-sm">
                  <Badge color="gray">{run.id}</Badge>
                </TableCell>
                <TableCell>{runStatusIcon(run.status)}</TableCell>
                <TableCell className="text-sm text-gray-700 dark:text-gray-300">{formatRunType(run.type)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">{formatDate(run.createdAt)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">
                  {run.completedAt ? formatDate(run.completedAt) : '—'}
                </TableCell>
                <TableCell>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CopyToClipboard
                      text={run.id}
                      tooltip="Copy Run ID"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
